import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatTime, calcDuration } from '../utils/dateUtils';
import StatusBadge from '../components/StatusBadge';

const Records = () => {
  const { hasRole, hasMinRole, canViewAllOffices } = useAuth();
  const isGlobal = canViewAllOffices();
  const isGuard  = hasRole('guard');

  const [visits, setVisits]     = useState([]);
  const [total, setTotal]       = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);

  const [offices, setOffices] = useState([]);

  const [filters, setFilters] = useState({
    search: '', status: isGuard ? 'active' : '', purpose: '', date: '', startDate: '', endDate: '', officeId: '',
  });

  const [checkoutModal, setCheckoutModal] = useState(null); // visit being checked out
  const [checkoutRemarks, setCheckoutRemarks]  = useState('');
  const [checkoutLoading, setCheckoutLoading]  = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchVisits = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 15, ...filters });
      // Remove empty params
      [...params.keys()].forEach(k => !params.get(k) && params.delete(k));
      const { data } = await api.get(`/visits?${params}`);
      setVisits(data.visits);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchVisits(1); setPage(1); }, [filters]); // eslint-disable-line
  useEffect(() => { fetchVisits(page); }, [page]);             // eslint-disable-line

  useEffect(() => {
    if (isGlobal) {
      api.get('/offices').then(({ data }) => setOffices(data.offices || [])).catch(() => {});
    }
  }, [isGlobal]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: value }));
  };

  const resetFilters = () => setFilters({ search: '', status: '', purpose: '', date: '', startDate: '', endDate: '', officeId: '' });

  /* ── Checkout ── */
  const openCheckout = (visit) => { setCheckoutModal(visit); setCheckoutRemarks(''); };

  const confirmCheckout = async () => {
    setCheckoutLoading(true);
    try {
      await api.patch(`/visits/${checkoutModal._id}/checkout`, {
        remarks: checkoutRemarks,
      });
      toast.success(`✅ ${checkoutModal.visitor?.name} checked out successfully`);
      setCheckoutModal(null);
      fetchVisits(page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Checkout failed');
    } finally {
      setCheckoutLoading(false);
    }
  };

  /* ── Delete ── */
  const confirmDelete = async () => {
    try {
      await api.delete(`/visits/${deleteTarget._id}`);
      toast.success('Record deleted');
      setDeleteTarget(null);
      fetchVisits(page);
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="container-fluid py-4 px-3 px-lg-4">
      {/* Header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-0">Visitor Records</h4>
          <p className="text-muted small mb-0">
            {total} record{total !== 1 ? 's' : ''} found
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-3">
              <div className="input-group input-group-sm">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input type="text" className="form-control" placeholder="Search name, mobile, serial…"
                  name="search" value={filters.search} onChange={handleFilterChange} />
              </div>
            </div>
            <div className="col-6 col-md-2">
              <select className="form-select form-select-sm" name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="col-6 col-md-2">
              <select className="form-select form-select-sm" name="purpose" value={filters.purpose} onChange={handleFilterChange}>
                <option value="">All Purpose</option>
                <option value="Official">Official</option>
                <option value="Personal">Personal</option>
                <option value="Meeting">Meeting</option>
                <option value="Interview">Interview</option>
                <option value="Vendor Visit">Vendor Visit</option>
              </select>
            </div>
            <div className="col-6 col-md-2">
              <input type="date" className="form-control form-control-sm" name="date" value={filters.date} onChange={handleFilterChange} placeholder="Filter by date" />
            </div>
            {isGlobal && (
              <div className="col-6 col-md-2">
                <select className="form-select form-select-sm" name="officeId" value={filters.officeId} onChange={handleFilterChange}>
                  <option value="">All Offices</option>
                  {offices.map(o => <option key={o._id} value={o._id}>{o.name} ({o.code})</option>)}
                </select>
              </div>
            )}
            <div className="col-6 col-md-2">
              <button className="btn btn-sm btn-outline-secondary w-100" onClick={resetFilters}>
                <i className="bi bi-x-circle me-1"></i>Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Records Table */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <span className="spinner-border text-primary"></span>
              <p className="text-muted mt-2 mb-0">Loading records…</p>
            </div>
          ) : visits.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox fs-1 d-block opacity-25 mb-2"></i>
              No records found. Try adjusting your filters.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4 py-3" style={{ minWidth: 110 }}>Serial No.</th>
                    <th style={{ minWidth: 160 }}>Visitor</th>
                    <th style={{ minWidth: 110 }}>Mobile</th>
                    <th style={{ minWidth: 140 }}>Meeting</th>
                    <th style={{ minWidth: 90 }}>Purpose</th>
                    <th style={{ minWidth: 90 }}>Date</th>
                    <th style={{ minWidth: 80 }}>Time In</th>
                    <th style={{ minWidth: 90 }}>Time Out</th>
                    <th style={{ minWidth: 80 }}>Duration</th>
                    {isGlobal && <th style={{ minWidth: 110 }}>Office</th>}
                    <th style={{ minWidth: 110 }}>Status</th>
                    <th className="pe-4 text-end" style={{ minWidth: 130 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.map(v => (
                    <tr key={v._id}>
                      <td className="ps-4 font-monospace text-primary fw-semibold">{v.serialNumber}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center fw-bold text-primary flex-shrink-0" style={{ width: 32, height: 32, fontSize: 12 }}>
                            {v.visitor?.name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-semibold lh-1">
                              {v.visitor?.name}
                              {(v.totalVisitors || 1) > 1 && (
                                <span className="badge ms-2" style={{ background: '#F2652C', fontSize: '0.65rem' }}>
                                  <i className="bi bi-people-fill me-1"></i>+{v.totalVisitors - 1}
                                </span>
                              )}
                            </div>
                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>{v.visitor?.company || v.visitor?.address?.slice(0, 25)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="font-monospace">{v.visitor?.phone}</td>
                      <td>
                        <div className="fw-semibold lh-1">{v.toWhomMeet}</div>
                        {v.department && <div className="text-muted" style={{ fontSize: '0.7rem' }}>{v.department}</div>}
                      </td>
                      <td>
                        <span className={`badge ${v.purpose === 'Personal' ? 'bg-warning text-dark' : 'bg-primary'}`}>
                          {v.purposeDetails || v.purpose}
                        </span>
                      </td>
                      <td className="text-muted">{formatDate(v.date)}</td>
                      <td className="fw-semibold">{formatTime(v.timeIn)}</td>
                      <td className={v.timeOut ? '' : 'text-muted fst-italic'}>{v.timeOut ? formatTime(v.timeOut) : 'Inside'}</td>
                      <td className="text-muted">{calcDuration(v.timeIn, v.timeOut)}</td>
                      {isGlobal && (
                        <td>
                          <span className="badge bg-light text-dark border" style={{ fontSize: '0.65rem' }}>{v.office?.code || '—'}</span>
                          <div className="text-muted" style={{ fontSize: '0.65rem' }}>{v.office?.city}</div>
                        </td>
                      )}
                      <td><StatusBadge status={v.status} /></td>
                      <td className="pe-4 text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          {v.status === 'active' && (isGuard || hasMinRole('location_admin')) && (
                            <button className="btn btn-sm btn-success px-2" title="Check Out" onClick={() => openCheckout(v)}>
                              <i className="bi bi-box-arrow-right"></i>
                            </button>
                          )}
                          {hasRole('superadmin') && (
                            <button className="btn btn-sm btn-outline-danger px-2" title="Delete" onClick={() => setDeleteTarget(v)}>
                              <i className="bi bi-trash3"></i>
                            </button>
                          )}
                          {!isGuard && (
                            <button className="btn btn-sm btn-outline-secondary px-2" title="View Details"
                              onClick={() => alert(`Serial: ${v.serialNumber}\nVisitor: ${v.visitor?.name}\nRemarks: ${v.remarks || 'None'}`)}>
                              <i className="bi bi-eye"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer bg-transparent border-top-0 px-4 py-3 d-flex align-items-center justify-content-between">
            <span className="text-muted small">Page {page} of {totalPages} · {total} total records</span>
            <nav>
              <ul className="pagination pagination-sm mb-0 gap-1">
                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                  <button className="page-link rounded-2" onClick={() => setPage(p => p - 1)}>
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                  return (
                    <li key={p} className={`page-item ${page === p ? 'active' : ''}`}>
                      <button className="page-link rounded-2" onClick={() => setPage(p)}>{p}</button>
                    </li>
                  );
                })}
                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link rounded-2" onClick={() => setPage(p => p + 1)}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* ── Checkout Modal ── */}
      {checkoutModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 10000 }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content rounded-4 border-0 shadow-lg">
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-box-arrow-right text-success me-2"></i>Record Exit — Visitor Checkout
                </h5>
                <button className="btn-close" onClick={() => setCheckoutModal(null)}></button>
              </div>
              <div className="modal-body px-4 py-3">
                {/* Visit Summary */}
                <div className="bg-light rounded-3 p-3 mb-4 small">
                  <div className="row g-2">
                    <div className="col-4 text-muted">Serial No.</div><div className="col-8 fw-semibold font-monospace">{checkoutModal.serialNumber}</div>
                    <div className="col-4 text-muted">Visitor</div><div className="col-8 fw-semibold">{checkoutModal.visitor?.name}{(checkoutModal.totalVisitors || 1) > 1 ? ` +${checkoutModal.totalVisitors - 1}` : ''}</div>
                    <div className="col-4 text-muted">Group Size</div><div className="col-8">{checkoutModal.totalVisitors || 1} {(checkoutModal.totalVisitors || 1) > 1 ? 'people (all checkout together)' : 'person'}</div>
                    <div className="col-4 text-muted">Mobile</div><div className="col-8">{checkoutModal.visitor?.phone}</div>
                    <div className="col-4 text-muted">Meeting</div><div className="col-8">{checkoutModal.toWhomMeet}</div>
                    <div className="col-4 text-muted">Time In</div><div className="col-8">{formatTime(checkoutModal.timeIn)}</div>
                    <div className="col-4 text-muted">Time Out</div><div className="col-8 text-success fw-bold">{new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label fw-semibold small">Exit Remarks</label>
                    <textarea className="form-control" rows={2} placeholder="Any remarks on exit…"
                      value={checkoutRemarks} onChange={e => setCheckoutRemarks(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 px-4 pb-4 pt-0 gap-2">
                <button className="btn btn-outline-secondary" onClick={() => setCheckoutModal(null)}>Cancel</button>
                <button className="btn btn-success fw-semibold px-4" onClick={confirmCheckout} disabled={checkoutLoading}>
                  {checkoutLoading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-check2-circle me-2"></i>}
                  Confirm Exit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteTarget && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 10000 }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-4 border-0 shadow-lg">
              <div className="modal-body text-center p-5">
                <div className="mb-3">
                  <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h5 className="fw-bold">Delete this record?</h5>
                <p className="text-muted mb-0">
                  <strong>{deleteTarget.visitor?.name}</strong> — {deleteTarget.serialNumber}<br />
                  This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer border-0 justify-content-center gap-2 pb-4">
                <button className="btn btn-outline-secondary px-4" onClick={() => setDeleteTarget(null)}>Cancel</button>
                <button className="btn btn-danger px-4 fw-semibold" onClick={confirmDelete}>
                  <i className="bi bi-trash3 me-2"></i>Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Records;
